import React, { Fragment, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  allChannelStateKeys,
  ChannelStateChip,
  defaultChannelFlow,
} from "@/components/badge/channel-state";
import { StateBadge } from "@/components/badge/state-badge";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { useApp } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { withCurrentTime } from "@/lib/date";
import { toastError } from "@/lib/toast";
import { mutateRequest, postFetcher, swrKey } from "@/lib/swr";

type MatrixVersion = {
  id: string;
  versionName?: string;
  versionCode?: string;
  state?: number;
};
type MatrixChannel = {
  id: number;
  name: string;
  playName?: string | null;
  flavorPid?: string | null;
};

interface MatrixData {
  channels: MatrixChannel[];
  versions: MatrixVersion[];
  cells: {
    versionId: string;
    channelId: number;
    state?: string | null;
    percent?: number | null;
  }[];
}

// Matrix view of the channel tab: rows are the releasable/in-flight
// versions, columns the app's channels, each cell the version×channel walk's
// current node (latest record) with its rollout percent. Data comes from
// GET /channels/matrix in one shot; cells click through to the channel page.
export const ChannelMatrix = ({ pid }: { pid: string }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const app = useApp(pid);
  const { data, isLoading } = useSWR<MatrixData>(swrKey("/channels/matrix", { pid }));

  // 交叉格点开记录弹窗:推进该 version×channel 的 walk。已有记录时节点限
  // 当前节点自身+nexts;空格开新 walk,全节点可选
  const flowMap = app?.channelStateFlow ?? defaultChannelFlow();
  const flowNodes = allChannelStateKeys().filter((s) => s in flowMap);
  const [editing, setEditing] = useState<{
    version: MatrixVersion;
    channel: MatrixChannel;
    nodes: string[];
  } | null>(null);
  const [node, setNode] = useState("");
  const [date, setDate] = useState("");
  const [percent, setPercent] = useState("");
  const { trigger: createRelease, isMutating } = useSWRMutation(
    "/channel-releases",
    postFetcher,
    {
      onSuccess: () => {
        mutateRequest("/channels/matrix");
        mutateRequest("/channels/overview");
        mutateRequest("/channel-releases");
        setEditing(null);
      },
      onError: (error: unknown) => toastError(toast, "记录发布失败", error),
    }
  );

  if (isLoading || !data) return <ListLoading />;
  if (!data.channels.length)
    return <ListEmpty>该 App 还没有配置渠道。</ListEmpty>;
  if (!data.versions.length)
    return <ListEmpty>没有满足发版门槛或在途的版本。</ListEmpty>;

  // Columns ordered by flavor pid (numeric), same as the list view.
  const channels = [...data.channels].sort((a, b) => {
    const pa = a.flavorPid ? Number(a.flavorPid) : Infinity;
    const pb = b.flavorPid ? Number(b.flavorPid) : Infinity;
    return pa - pb || a.id - b.id;
  });
  const cellByPair = new Map(
    data.cells.map((c) => [`${c.versionId}:${c.channelId}`, c])
  );

  return (
    <div className="overflow-x-auto rounded-md border">
      <div
        className="grid min-w-max text-sm"
        style={{
          gridTemplateColumns: `minmax(9rem,auto) repeat(${data.versions.length}, minmax(6.5rem,1fr))`,
        }}
      >
        {/* Header row: corner + version columns (newest first). */}
        <div className="border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
          渠道 \ 版本
        </div>
        {data.versions.map((v) => (
          <div
            key={v.id}
            className="flex cursor-pointer flex-col items-center gap-1 border-b border-l bg-muted/50 px-2 py-1.5 text-xs font-medium hover:underline"
            onClick={() => navigate(`/apps/${pid}/versions/${v.id}`)}
          >
            {/* 名称与状态徽章分两行,避免列被撑开 */}
            <span className="max-w-full truncate">
              {v.versionName ?? v.id}
              {v.versionCode && `(${v.versionCode})`}
            </span>
            {v.state !== undefined && <StateBadge state={v.state} />}
          </div>
        ))}
        {channels.map((c) => (
          <Fragment key={c.id}>
            <div
              title={c.playName ?? c.name}
              className="flex cursor-pointer items-center truncate border-b px-3 py-1.5 hover:underline"
              onClick={() => navigate(`/apps/${pid}/channels/${c.id}`)}
            >
              {c.name}
            </div>
            {data.versions.map((v) => {
              const cell = cellByPair.get(`${v.id}:${c.id}`);
              return (
                <div
                  key={v.id}
                  className="flex cursor-pointer items-center justify-center gap-1 border-b border-l px-2 py-1.5 hover:bg-muted/50"
                  onClick={() => {
                    setNode("");
                    setDate(format(new Date(), "yyyy-MM-dd"));
                    setPercent("");
                    setEditing({
                      version: v,
                      channel: c,
                      nodes: cell?.state
                        ? [cell.state, ...(flowMap[cell.state] ?? [])]
                        : flowNodes,
                    });
                  }}
                >
                  {cell?.state ? (
                    <>
                      <ChannelStateChip state={cell.state} />
                      {/* 百分比只对放量中有信息量;线上=100 是应有之义不显示 */}
                      {cell.state === "rolling" && cell.percent != null && (
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {cell.percent}%
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              记录 {editing?.channel.name} ·{" "}
              {editing?.version.versionName ?? editing?.version.id} 发布
            </DialogTitle>
            <DialogDescription>
              推进该版本在此渠道的 channel flow:选择本次走到的节点,记一条即走一步。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">流转到</span>
              <Select value={node} onValueChange={setNode}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="选择 channel flow 节点…" />
                </SelectTrigger>
                <SelectContent>
                  {(editing?.nodes ?? []).map((st) => (
                    <SelectItem key={st} value={st}>
                      <span className="flex items-center gap-2">
                        <ChannelStateChip state={st} />
                        <span className="font-mono text-xs text-muted-foreground">
                          {st}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <label className="block flex-1 space-y-1">
                <span className="text-sm text-muted-foreground">发布日期</span>
                <Input
                  type="date"
                  value={date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDate(e.target.value)
                  }
                  className="h-8"
                />
              </label>
              {/* 只有 rolling 带比例且必填;其余节点不记 */}
              {node === "rolling" && (
                <label className="block w-24 space-y-1">
                  <span className="text-sm text-muted-foreground">百分比</span>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={percent}
                    placeholder="50"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPercent(e.target.value)
                    }
                    className="h-8 tabular-nums"
                  />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(null)}
            >
              取消
            </Button>
            <Button
              size="sm"
              disabled={
                isMutating ||
                !node ||
                !date ||
                (node === "rolling" && !percent.trim())
              }
              onClick={() =>
                editing &&
                createRelease({
                  versionId: editing.version.id,
                  channelId: editing.channel.id,
                  releasedAt: withCurrentTime(date),
                  ...(node === "rolling"
                    ? { percent: Number(percent.trim()) }
                    : {}),
                  state: node,
                })
              }
            >
              {isMutating ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelMatrix;
